import React, { useState, useEffect } from 'react';
import { X, Brain, Clock, Star, CheckCircle, AlertCircle, Code, Lightbulb, Wrench, TrendingUp, TrendingDown, Download, Copy, Share2 } from 'lucide-react';
import { Agent } from '../lib/supabase';

interface AgentAnalysis {
  id: string;
  agent_id: string;
  usefulness_score: number;
  complexity_level: 'beginner' | 'intermediate' | 'advanced';
  capabilities: string[];
  use_cases: string[];
  step_by_step_guide: StepGuide[];
  requirements: string[];
  installation_steps: string[];
  pros: string[];
  cons: string[];
  estimated_setup_time: string;
  last_analyzed: string;
}

interface StepGuide {
  step_number: number;
  title: string;
  description: string;
  code_example?: string;
  notes?: string;
}

interface AgentAnalysisModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentAnalysisModal({ agent, isOpen, onClose }: AgentAnalysisModalProps) {
  const [analysis, setAnalysis] = useState<AgentAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'guide' | 'details'>('overview');
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && agent.id) {
      analyzeAgent();
    }
  }, [isOpen, agent.id]);

  const analyzeAgent = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-analyzer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agent.id,
            force_reanalyze: false
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setAnalysis(data.analysis);
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getComplexityColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'text-green-600 bg-green-50';
      case 'intermediate': return 'text-yellow-600 bg-yellow-50';
      case 'advanced': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const copyToClipboard = (text: string, item: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const downloadSetupGuide = () => {
    if (!analysis) return;
    
    const guideContent = `# Setup Guide for ${agent.name}

## Overview
- Usefulness Score: ${analysis.usefulness_score}/100
- Complexity Level: ${analysis.complexity_level}
- Estimated Setup Time: ${analysis.estimated_setup_time}

## Capabilities
${analysis.capabilities.map(cap => `- ${cap}`).join('\n')}

## Requirements
${analysis.requirements.map(req => `- ${req}`).join('\n')}

## Step-by-Step Guide
${analysis.step_by_step_guide.map(step => `
### Step ${step.step_number}: ${step.title}
${step.description}

${step.code_example ? `\`\`\`\n${step.code_example}\n\`\`\`` : ''}

${step.notes ? `**Note:** ${step.notes}` : ''}
`).join('\n')}

## Pros
${analysis.pros.map(pro => `- ${pro}`).join('\n')}

## Cons
${analysis.cons.map(con => `- ${con}`).join('\n')}
`;
    
    const blob = new Blob([guideContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agent.name.toLowerCase().replace(/\s+/g, '-')}-setup-guide.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Agent Analysis</h2>
              <p className="text-sm text-gray-600">{agent.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Brain className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
                <p className="text-gray-600">Analyzing agent capabilities...</p>
                <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Analysis Failed</span>
                </div>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <button
                  onClick={analyzeAgent}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Retry Analysis
                </button>
              </div>
            </div>
          ) : analysis ? (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  {[
                    { id: 'overview', label: 'Overview', icon: Star },
                    { id: 'guide', label: 'Setup Guide', icon: Code },
                    { id: 'details', label: 'Details', icon: Wrench }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Score and Complexity */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-900">Usefulness Score</span>
                        </div>
                        <div className={`text-3xl font-bold ${getScoreColor(analysis.usefulness_score)}`}>
                          {analysis.usefulness_score}/100
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-900">Complexity</span>
                        </div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${getComplexityColor(analysis.complexity_level)}`}>
                          {analysis.complexity_level}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">
                          Setup time: {analysis.estimated_setup_time}
                        </p>
                      </div>
                    </div>

                    {/* Capabilities */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Capabilities</h3>
                      <div className="flex flex-wrap gap-2">
                        {analysis.capabilities.map((capability, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                          >
                            {capability}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Use Cases */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Use Cases</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {analysis.use_cases.map((useCase, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{useCase}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pros and Cons */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          Pros
                        </h3>
                        <div className="space-y-2">
                          {analysis.pros.map((pro, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{pro}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <TrendingDown className="w-5 h-5 text-red-600" />
                          Cons
                        </h3>
                        <div className="space-y-2">
                          {analysis.cons.map((con, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{con}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button
                        onClick={downloadSetupGuide}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Setup Guide
                      </button>
                      <button
                        onClick={() => copyToClipboard(JSON.stringify(analysis, null, 2), 'analysis')}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        {copiedItem === 'analysis' ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Analysis Data
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveTab('guide')}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <Code className="w-4 h-4" />
                        View Setup Guide
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'guide' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-medium text-blue-900 mb-2">Step-by-Step Setup Guide</h3>
                      <p className="text-blue-700 text-sm">
                        Follow these steps to get {agent.name} up and running. Estimated time: {analysis.estimated_setup_time}
                      </p>
                    </div>

                    {analysis.step_by_step_guide.map((step, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {step.step_number}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-2">{step.title}</h4>
                            <p className="text-gray-700 text-sm mb-3">{step.description}</p>
                            
                            {step.code_example && (
                              <div className="relative">
                                <div className="bg-gray-900 text-gray-100 rounded-lg p-3 text-sm font-mono overflow-x-auto">
                                  <pre>{step.code_example}</pre>
                                </div>
                                <button
                                  onClick={() => copyToClipboard(step.code_example, `step-${step.step_number}`)}
                                  className="absolute top-2 right-2 p-1 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                                  title="Copy to clipboard"
                                >
                                  {copiedItem === `step-${step.step_number}` ? (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            )}
                            
                            {step.notes && (
                              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-yellow-800 text-sm">
                                  <strong>Note:</strong> {step.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button
                        onClick={downloadSetupGuide}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Guide as Markdown
                      </button>
                      <button
                        onClick={() => window.open(agent.repository_url || agent.source_url, '_blank')}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <Code className="w-4 h-4" />
                        View Source Code
                      </button>
                      <button
                        onClick={() => setActiveTab('overview')}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Back to Overview
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Requirements */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          {analysis.requirements.map((req, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Analysis Metadata */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Analysis Details</h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Last Analyzed:</span>
                          <span className="text-gray-900">
                            {new Date(analysis.last_analyzed).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Analysis ID:</span>
                          <span className="text-gray-900 font-mono text-xs">
                            {analysis.id.slice(0, 8)}...
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Re-analyze Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => analyzeAgent()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Re-analyze Agent
                      </button>
                      <p className="text-xs text-gray-500 mt-2">
                        Force a fresh analysis of this agent's capabilities and documentation
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
        
        {/* Footer */}
        {!loading && analysis && (
          <div className="border-t border-gray-200 p-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}